import React, { useContext, useState } from 'react';

import { Form, Formik } from 'formik';
import { useToasts } from 'react-toast-notifications';

import Button from '@components/common/Button/Button';
import DeletePotButton from '@components/common/Button/DeletePotButton/DeletePotButton';
import Input from '@components/common/Formik/FormikInputField';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import Switch from '@components/common/Switch/Switch';
import Text from '@components/common/Text/Text';
import PotContext from '@components/lpc/Context/Pot/PotContext';
import Objective from '@components/lpc/Pot/AmountModule/Objective/Objective';
import { personRoles } from '@models/PotModel/personRoles';
import { statusPO } from '@models/PotModel/statusPO';
import { potObjectiveType, potType } from '@propTypes/potTypes';
import PotService from '@services/domain/Lpc/PotService';
import { formatDateToInternational } from '@services/utils/DateService';
import { getAbsoluteUrl } from '@services/utils/StringService';
import { potSettingsGeneral } from '@validations/potSchema';

import css from './GeneralForm.module.scss';

type GeneralFormProps = {
    onCloseModal: () => void;
    newPoolObjective: (potObjectiveType) => void;
    potRef: string;
    pot?: potType;
    potLink: string;
    customUrl: string;
    potStatus: string;
    amountLeft: number;
    askForMessage: boolean;
};

const GeneralForm = ({
    onCloseModal,
    newPoolObjective,
    potRef,
    pot,
    potLink,
    customUrl,
    potStatus,
    askForMessage,
    amountLeft,
}: GeneralFormProps) => {
    const { t } = useTranslateLpc(['lpc-pot', 'common']);
    const { addToast } = useToasts();
    const potContext = useContext(PotContext);
    const { getUpdatedPot } = potContext;

    const [isLoading, setIsLoading] = useState(false);
    const [objectiveDate, setObjectiveDate] = useState<any>(
        pot.poolObjective && pot.poolObjective.dateLimit
            ? formatDateToInternational(pot.poolObjective.dateLimit, '-')
            : null,
    );
    const [objectiveAmount, setObjectiveAmount] = useState<number>(pot.poolObjective?.amountToReach || null);

    const [initialAskForMessage, setInitialAskForMessage] = useState(askForMessage);
    const [initialPausePot, setInitialPausePot] = useState(pot.status === 'CLOSED');

    const getInitialValues = () => {
        return {
            customUrlSuffixPO: customUrl,
            dateLimit: objectiveDate,
            amountToReach: objectiveAmount,
        };
    };

    const onUpdatePot = (values) => {
        const objective: potObjectiveType = {
            dateLimit: objectiveDate == null ? null : formatDateToInternational(objectiveDate, '-'),
            amountToReach: objectiveAmount || 0,
        };
        const body = {
            askForMessage: initialAskForMessage,
            customUrlSuffixPO: values.customUrlSuffixPO,
            statusPO: initialPausePot ? statusPO.CLOSED : statusPO.RUNNING,
            objective: objective,
        };
        setIsLoading(true);
        PotService.updatePotSettingsGeneral(potRef, body)
            .then((res) => {
                res.status === 200 && getUpdatedPot(potRef);
                addToast(t(`success.UPDATE_SUCCESS`), {
                    appearance: 'success',
                    autoDismiss: true,
                });
                pot.status = body.statusPO;
                pot.customUniq = body.customUrlSuffixPO;
                pot.askForMessage = body.askForMessage;
                pot.poolObjective = body.objective;
                newPoolObjective(body.objective);
            })
            .then(() => onCloseModal())
            .finally(() => setIsLoading(false))
            .catch(() =>
                addToast(t(`errors.DEFAULT_ERROR_MESSAGE`), {
                    appearance: 'error',
                    autoDismiss: true,
                }),
            );
    };

    const onCancel = () => {
        onCloseModal();
    };

    return (
        <Formik
            validationSchema={potSettingsGeneral}
            initialValues={getInitialValues()}
            onSubmit={(values) => onUpdatePot(values)}
        >
            {() => (
                <Form>
                    <div className={css.form}>
                        {/* CUSTOM URL */}
                        <Text variant={'mini_label'} color={'txt-primary'}>
                            {t('settings.general.url-title')}
                        </Text>
                        <Text variant={'caption_01'} color={'txt-primary'}>
                            {t('settings.general.url-desc')}
                        </Text>
                        <div className={css.form__url}>
                            <Text customClass={css.form__url__txt} variant={'caption_02'} color={'txt-primary'}>
                                {getAbsoluteUrl(potLink) + '-'}
                            </Text>
                            <Input name={'customUrlSuffixPO'} type={'text'} />
                        </div>
                        <div className={css.form__divider} />

                        {/* OBJECTIVE */}
                        {pot.personRole !== personRoles.BENEFICIARY && (
                            <div className="Objective">
                                <Objective
                                    potRef={pot.uniq}
                                    pot={pot}
                                    handleDateChange={(name, value) => {
                                        setObjectiveDate(value);
                                    }}
                                    handleAmountChange={(value) => {
                                        setObjectiveAmount(value);
                                    }}
                                    personRole={pot.personRole}
                                />
                                <div className={css.form__divider} />
                            </div>
                        )}

                        {/* SWITCH CONTACT */}
                        <div className={css.form__switch}>
                            <Text variant={'mini_label'} color={'txt-primary'}>
                                {t('settings.general.contact-title')}
                            </Text>
                            <Switch
                                customClass={css.side__nav__tag__switch}
                                defaultOn={initialAskForMessage}
                                onChange={(bool) => setInitialAskForMessage(bool)}
                            />
                        </div>
                        <Text variant={'caption_01'} color={'txt-primary'}>
                            {t('settings.general.contact-desc')}
                        </Text>
                        <div className={css.form__divider} />
                        {/* PAUSE POT */}
                        {(pot.category !== 'MPP' || potStatus === statusPO.RUNNING) && (
                            <>
                                <div className={css.form__switch}>
                                    <Text variant={'mini_label'} color={'txt-primary'}>
                                        {t('settings.general.pause-title')}
                                    </Text>
                                    <Switch
                                        customClass={css.side__nav__tag__switch}
                                        defaultOn={initialPausePot}
                                        onChange={(bool) => setInitialPausePot(bool)}
                                    />
                                </div>
                                <Text variant={'caption_01'} color={'txt-primary'}>
                                    {t('settings.general.pause-desc')}
                                </Text>
                                <div className={css.form__divider} />
                            </>
                        )}

                        {/* DELETE POT */}
                        <div className={css.form__switch}>
                            <Text variant={'mini_label'} color={'txt-primary'}>
                                {t('settings.general.delete-title')}
                            </Text>
                            <DeletePotButton potRef={potRef} amountLeft={amountLeft} customClass={css.form__cta__error}>
                                <img src={'/front-static/icons/action/delete-primary.svg'} alt="delete" />
                            </DeletePotButton>
                        </div>
                        <Text variant={'caption_01'} color={'txt-primary'}>
                            {t('settings.general.delete-desc')}
                        </Text>
                        {/* SUBMIT */}
                        <div className={css.form__submit}>
                            <Button
                                type={'button'}
                                customClass={css.form__submit__cta}
                                variant={'secondary'}
                                onClick={() => onCancel()}
                            >
                                {t('settings.cancel')}
                            </Button>
                            <Button
                                type={'submit'}
                                margin={'0 0 0 8px'}
                                variant={'primary'}
                                customClass={css.form__submit__cta}
                                isLoading={isLoading}
                                isDisabled={isLoading}
                            >
                                {t('settings.confirm')}
                            </Button>
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default GeneralForm;
